/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import jwt from 'jsonwebtoken';
import Vue from 'vue';
import Router from 'vue-router';
import {
  READ_PERMISSIONS,
  ROUTER_PERMISSIONS,
} from './constants/router.permissions';
import ability from './services/ability';
import autoApi from './services/auto.api';
import { STATUS_PROPOSTA } from './utils/constants';
import functions from './utils/functions';
Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      redirect: '/dashBoard',
      beforeEnter: navigationGuard,
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/dashBoard',
          name: 'Dashboard',
          beforeEnter: navigationGuard,
          component: () => import('./views/Home/Home2.vue'),
          meta: {
            checkPermission: false,
          },
        },
        {
          path: '/segurados',
          name: 'Associados',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/Associados.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Segurados',
                active: true,
              },
            ],
            pageTitle: 'Segurados',
            rule: 'editor',
          },
        },
        {
          path: '/DetalheSegurado/:idAssociado',
          name: 'DetalheAssociado',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/Associado-Detalhe.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.INSURED_DETAIL,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Segurados',
                url: '/segurados',
              },
              {
                title: 'Detalhes',
                active: true,
              },
            ],
            pageTitle: 'Detalhes do Segurado',
          },
        },
        {
          path: '/Cotacoes',
          name: 'Cotações',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/Cotacoes.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel de Cotações/Propostas',
                active: true,
              },
            ],
            pageTitle: 'Painel de Cotações/Propostas',
            rule: 'editor',
          },
        },
        {
          path: '/auto/cotacao',
          name: 'novaCotacaoAuto',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/novaCotacaoAuto.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_NEW,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cotações',
                url: '/Cotacoes',
              },
              {
                title: 'Nova Cotação Auto',
                active: true,
              },
            ],
            pageTitle: 'Nova Cotação Auto',
          },
        },
        {
          path: '/auto/cotacao/:id',
          name: 'novaCotacaoAuto',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/novaCotacaoAuto.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_DETAIL,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cotações',
                url: '/Cotacoes',
              },
              {
                title: 'Nova Cotação Auto',
                active: true,
              },
            ],
            pageTitle: 'Nova Cotação Auto',
          },
        },
        {
          path: '/rcvat/cotacao',
          name: 'novaPropostaRcvat',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/novaPropostaRcvat.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_NEW,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Nova Proposta RCVAT',
                active: true,
              },
            ],
            pageTitle: 'Nova Proposta RCVAT',
          },
        },
        {
          path: '/vida/cotacao',
          name: 'novaPropostaVida',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/novaPropostaVida.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_NEW,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Nova Proposta Vida',
                active: true,
              },
            ],
            pageTitle: 'Nova Proposta Vida',
          },
        },
        {
          path: '/pagamentos',
          name: 'Pagamentos',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/financeiro/Pagamentos/Payments.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Pagamentos',
                active: true,
              },
            ],
            pageTitle: 'Pagamentos',
            rule: 'editor',
          },
        },
        {
          path: '/boletos',
          name: 'Boletos',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/financeiro/Boletos/BilletsReport.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Boletos',
                active: true,
              },
            ],
            pageTitle: 'Boletos',
            rule: 'editor',
          },
        },
        {
          path: '/relatorio-parcelas-recebidas',
          name: 'Parcelas Recebidas',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/financeiro/parcelas-recebidas/parcelasRecebidas.vue'
            ),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Parcelas Recebidas',
                active: true,
              },
            ],
            pageTitle: 'Parcelas Recebidas',
            rule: 'editor',
          },
        },
        {
          path: '/restituicoes',
          name: 'Restituições',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/financeiro/Refunds/refunds.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Restituições',
                active: true,
              },
            ],
            pageTitle: 'Restituições',
            rule: 'editor',
          },
        },
        {
          path: '/fechamento',
          name: 'Fechamento',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/financeiro/Fechamento/FechamentoView.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Financeiro',
              },
              {
                title: 'Fechamento',
                active: true,
              },
            ],
            pageTitle: 'Fechamento',
            rule: 'editor',
          },
        },
        {
          path: '/Extrato',
          name: 'Extrato',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/financeiro/Extrato.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Extrato',
                active: true,
              },
            ],
            pageTitle: 'Extrato',
            rule: 'editor',
          },
        },
        {
          path: '/PainelFinanceiro',
          name: 'Painel Financeiro',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/financeiro/PainelFinanceiro.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Financeiro',
                active: true,
              },
            ],
            pageTitle: 'Painel Financeiro',
            rule: 'editor',
          },
        },
        {
          path: '/ArquivoRemessa',
          name: 'Arquivo Remessa',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/financeiro/arquivoRemessa.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Arquivo Remessa', active: true },
            ],
            pageTitle: 'Arquivo Remessa',
            rule: 'editor',
          },
        },
        {
          path: '/ArquivoRetorno',
          name: 'Arquivo Retorno',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/financeiro/arquivoRetorno.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Arquivo Retorno', active: true },
            ],
            pageTitle: 'Arquivo Retorno',
            rule: 'editor',
          },
        },
        {
          path: '/Vistorias',
          name: 'Vistorias',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/Vistorias.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Vistorias',
                active: true,
              },
            ],
            pageTitle: 'Vistorias',
            rule: 'editor',
          },
        },
        {
          path: '/eventos',
          name: 'Eventos',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/eventos/Eventos-List/Eventos-List.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.EVENTS,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Painel Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/situacao-sinistro',
          name: 'Situação de Evento',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/eventos/situacoes-evento/situacoes-evento-list.vue'
            ),
          meta: {
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Sinistros',
                url: '/eventos',
              },
              {
                title: 'Situação de Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Situação de Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/motivo-sinistro',
          name: 'Motivo de Evento',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/eventos/motivos-evento/motivos-evento-list.vue'
            ),
          meta: {
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Sinistros',
                url: '/eventos',
              },
              {
                title: 'Motivo de Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Motivo de Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/detalheVistoria/:idCotacao',
          name: 'detalheVistoria',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/VistoriasDetalhes.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.INSPECTION_DETAIL,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Vistorias',
                url: '/Vistorias',
              },
              {
                title: 'Detalhe',
                active: true,
              },
            ],
            pageTitle: 'Detalhe',
            rule: 'editor',
          },
        },
        {
          path: '/auto/proposta/:id',
          name: 'editarProposta',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/editarPropostaAuto.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_DETAIL,
            rule: 'editor',
          },
        },
        {
          path: '/auto/apolice/:id/endosso',
          name: 'endosso',
          beforeEnter: function (to, from, next) {
            navigationGuard(to, from, next);
            const pageTitle = 'Endosso';
            to.meta.pageTitle = pageTitle;

            to.meta.breadcrumb = [
              { title: 'Home', url: '/' },
              { title: 'Propostas', url: '/Cotacoes' },
              { title: pageTitle, active: true },
            ];
          },
          component: () =>
            import('@/views/pages/Endorsement/EndorsementQuotation.vue'),
          meta: {
            rule: 'editor',
            checkPermission: false,
          },
        },
        {
          path: '/auto/endosso',
          name: 'novoEndosso',
          beforeEnter: function (to, from, next) {
            navigationGuard(to, from, next);
            const pageTitle = 'Novo Endosso';
            to.meta.pageTitle = pageTitle;

            to.meta.breadcrumb = [
              { title: 'Home', url: '/' },
              { title: 'Propostas', url: '/Cotacoes' },
              { title: pageTitle, active: true },
            ];
          },
          component: () =>
            import('@/views/pages/Endorsement/NewEndorsement.vue'),
          meta: {
            rule: 'editor',
            checkPermission: false,
          },
        },
        {
          path: '/editarCotacao2/:idCotacao',
          name: 'editarCotacao2',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/editarCotacao2.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.QUOTATION_DETAIL,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Propostas',
                url: '/Cotacoes',
              },
              {
                title: 'Editar',
                active: true,
              },
            ],
            pageTitle: 'Editar',
            rule: 'editor',
          },
        },
        {
          path: '/editar-evento/:idEvento',
          name: 'editarEvento-v2',
          beforeEnter: function (to, from, next) {
            navigationGuard(
              { name: 'Eventos', meta: { checkPermission: true } },
              from,
              next,
            );
          },
          component: () =>
            import('./views/menu/eventos/Eventos/EditarEventoView.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.EVENTS,
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Sinistro',
                url: '/eventos',
              },
              {
                title: 'Editar Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Editar Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/editar-eventos-terceiro/:idEvento/:idTerceiro',
          name: 'editar-eventos-terceiro',
          beforeEnter: function (to, from, next) {
            // Atualiza a URL do breadcrumb com o parâmetro idEvento
            const breadcrumb = to.meta.breadcrumb;
            const idEvento = to.params.idEvento;

            breadcrumb[2].title = `Sinistro - ${idEvento}`;
            breadcrumb[2].url = `/editar-evento/${idEvento}`;

            navigationGuard(
              { name: 'Eventos', meta: { checkPermission: true } },
              next,
            );
          },
          component: () =>
            import(
              './views/menu/eventos/Eventos-Terceiros/EditarTerceirosView.vue'
            ),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.EVENTS,
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Sinistro',
                url: '/eventos',
              },
              {
                title: 'Sinistro',
                url: '/editar-evento/:idEvento',
                active: true,
              },
              {
                title: 'Editar Terceiro',
                active: true,
              },
            ],
            pageTitle: 'Editar Terceiro',
            rule: 'editor',
          },
        },
        {
          path: '/editar-eventos-patrimonio/:idEvento/:idPatrimonio',
          name: 'editar-eventos-patrimonio',
          beforeEnter: function (to, from, next) {
            // Atualiza a URL do breadcrumb com o parâmetro idEvento
            const breadcrumb = to.meta.breadcrumb;
            const idEvento = to.params.idEvento;

            breadcrumb[2].title = `Evento - ${idEvento}`;
            breadcrumb[2].url = `/editar-evento/${idEvento}`;

            navigationGuard(
              { name: 'Eventos', meta: { checkPermission: true } },
              next,
            );
          },
          component: () =>
            import(
              './views/menu/eventos/Eventos-Patrimonio/EditarPatrimonioView.vue'
            ),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.EVENTS,
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Eventos',
                url: '/eventos',
              },
              {
                title: 'Evento',
                url: '/editar-evento/:idEvento',
                active: true,
              },
              {
                title: 'Editar Patrimônio',
                active: true,
              },
            ],
            pageTitle: 'Editar Patrimônio',
            rule: 'editor',
          },
        },
        {
          path: '/evento-analise/:idEvento',
          name: 'Análise de Eventos',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/eventos/Eventos-Analise/EventosAnaliseView.vue'
            ),
          meta: {
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Eventos',
                url: '/eventos',
              },
              {
                title: 'Análise do Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Análise do Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/novo-evento',
          name: 'novo-evento',
          beforeEnter: function (to, from, next) {
            navigationGuard(
              { name: 'Eventos', meta: { checkPermission: true } },
              next,
            );
          },
          component: () =>
            import('./views/menu/eventos/Novo-Evento/NovoEventoView.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.EVENTS,
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Painel Sinistro',
                url: '/eventos',
              },
              {
                title: 'Novo Sinistro',
                active: true,
              },
            ],
            pageTitle: 'Novo Sinistro',
            rule: 'editor',
          },
        },
        {
          path: '/Graficos/Crescimento',
          name: 'Crescimento',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/graficos/crescimento.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Crescimento de Base',
            rule: 'editor',
          },
        },
        {
          path: '/Graficos/Vendas',
          name: 'Vendas',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/graficos/vendas.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Vendas',
            rule: 'editor',
          },
        },
        {
          path: '/Graficos/Cancelamentos',
          name: 'Cancelamentos',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/graficos/cancelamentos.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Cancelamentos',
            rule: 'editor',
          },
        },
        {
          path: '/Consolidado',
          name: 'Consolidado',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/graficos/Consolidado.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                ignoreMaintenance: true,
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Painel de Vendas',
            rule: 'editor',
          },
        },
        {
          path: '/UsoDaContaCorrente',
          name: 'Uso da Conta Corrente',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/relatorios/current-account/CurrentAccountUse.vue'
            ),
          meta: {
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Uso da conta corrente',
            rule: 'editor',
          },
        },
        {
          path: '/auto-anual',
          name: 'Auto Anual',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/relatorios/individual-car/individial-car.vue'),
          meta: {
            breadcrumb: [
              {
                ignoreMaintenance: true,
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Auto Anual',
            rule: 'editor',
          },
        },
        {
          path: '/microvida',
          name: 'Microvida',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/relatorios/micro-life/MicroLife.vue'),
          meta: {
            breadcrumb: [
              {
                ignoreMaintenance: true,
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Microvida',
            rule: 'editor',
          },
        },
        {
          path: '/comissao',
          name: 'Comissão',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/relatorios/broker-commission/BrokerCommission.vue'
            ),
          meta: {
            ignoreMaintenance: true,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios',
                active: true,
              },
            ],
            pageTitle: 'Comissão',
            rule: 'editor',
          },
        },
        {
          path: '/Dinamicos',
          name: 'Dinamicos',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/relatorios/dinamicos/RelatoriosDinamicosView.vue'
            ),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Relatórios Dinâmicos',
                active: true,
              },
            ],
            pageTitle: 'Relatórios Dinâmicos',
            rule: 'editor',
          },
        },
        // =============================================================================
        // MENU ADMINISTRADOR (CADASTRO)
        // =============================================================================
        {
          path: '/empresa',
          name: 'Empresa',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/empresa.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Empresa',
            rule: 'editor',
          },
        },
        {
          path: '/usuarios',
          name: 'Usuário',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/usuarios.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Usuários',
            rule: 'editor',
          },
        },
        {
          path: '/vistoriadores',
          name: 'Vistoriador',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/vistoriadores.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Vistoriador',
            rule: 'editor',
          },
        },
        {
          path: '/pre-cadastro-corretoras',
          name: 'Pré-Cadastro Corretoras',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/cadastros/broker-pre-register/BrokerPreRegister.vue'
            ),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Pré-Cadastro Corretoras',
                active: true,
              },
            ],
            pageTitle: 'Pré-Cadastro Corretoras',
            rule: 'editor',
          },
        },
        {
          path: '/pre-cadastro-corretoras/:id',
          name: 'Pré-Cadastro Corretoras',
          beforeEnter: navigationGuard,
          component: () =>
            import(
              './views/menu/cadastros/broker-pre-register/components/BrokerPreRegisterEdit.vue'
            ),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Pré-Cadastro Corretoras',
                url: '/pre-cadastro-corretoras',
              },
              {
                title: 'Editar Pré-Cadastro Corretoras',
                active: true,
              },
            ],
            pageTitle: 'Editar Pré-Cadastro Corretoras',
            rule: 'editor',
          },
        },
        {
          path: '/corretoras',
          name: 'Corretora',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/regionais.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Corretoras',
            rule: 'editor',
          },
        },
        {
          path: '/assessorias',
          name: 'Assessoria',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/divisoes.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Assessorias',
            rule: 'editor',
          },
        },
        {
          path: '/convenio',
          name: 'Convênio',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/convenio.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Convênio',
            rule: 'editor',
          },
        },
        {
          path: '/setores',
          name: 'Setor',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/setores.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Setores',
            rule: 'editor',
          },
        },
        {
          path: '/tipoAtendimento',
          name: 'Tipo de Atendimento',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/tipoAtendimento.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Tipo de Atendimento',
            rule: 'editor',
          },
        },
        {
          path: '/perfis',
          name: 'Perfil de Usuário',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/perfis.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Perfil de Usuário',
            rule: 'editor',
          },
        },
        {
          path: '/contasBancarias',
          name: 'Contas Bancárias',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/contasBancarias.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Contas Bancárias',
            rule: 'editor',
          },
        },
        {
          path: '/carteiraCobranca',
          name: 'Carteira de Cobrança',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/cadastros/carteirasCobranca.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Carteira de Cobrança',
            rule: 'editor',
          },
        },
        {
          path: '/permissoes',
          name: 'Permissões',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/permissoes.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Permissões',
            rule: 'editor',
          },
        },
        {
          path: '/mensageria',
          name: 'Mensageria',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/cadastros/Mensageria/mensageria-list.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Mensageria',
                active: true,
              },
            ],
            pageTitle: 'Mensageria',
            rule: 'editor',
          },
        },
        {
          path: '/mensageria/novo',
          name: 'Mensageria',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/cadastros/Mensageria/mensageria-form.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.MESSAGING_EDIT,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Mensageria  ',
                url: '/mensageria',
              },
              {
                title: 'Cadastro Mensagem',
                active: true,
              },
            ],
            pageTitle: 'Cadastro Mensagem',
            rule: 'editor',
          },
        },
        {
          path: '/mensageria/editar/:idMensagem',
          name: 'Mensageria',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/cadastros/Mensageria/mensageria-form.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.MESSAGING_EDIT,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Mensageria  ',
                url: '/mensageria',
              },
              {
                title: 'Edição Mensagem',
                active: true,
              },
            ],
            pageTitle: 'Edição Mensagem',
            rule: 'editor',
          },
        },
        {
          path: '/fornecedor',
          name: 'Fornecedor',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/cadastros/fornecedor.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Fornecedor',
                active: true,
              },
            ],
            pageTitle: 'Fornecedor',
            rule: 'editor',
          },
        },
        {
          path: '/tiposFornecedor',
          name: 'Fornecedor',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/tiposFornecedor.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.SUPPLIER_TYPES,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Fornecedor',
                url: '/fornecedor',
              },
              {
                title: 'Tipo de Fornecedor',
                active: true,
              },
            ],
            pageTitle: 'Tipo de Fornecedor',
            rule: 'editor',
          },
        },
        {
          path: '/recusa-de-proposta',
          name: 'Recusa de Proposta',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/cadastros/ProposalRefuseReasons.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.PROPOSAL_REFUSE_REASONS,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Recusa de Proposta',
            rule: 'editor',
            checkPermission: true,
          },
        },

        // =============================================================================
        // MENU ADMINISTRADOR (CONFIGURAÇÕES)
        // =============================================================================

        {
          path: '/produtos',
          name: 'Produto',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/produtos.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'Produtos',
            rule: 'editor',
          },
        },
        {
          path: '/automovel-anual',
          name: 'Automóvel Anual',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/auto-anual/AutoAnual.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Automóvel Anual',
                active: true,
              },
            ],
            pageTitle: 'Automóvel Anual',
            rule: 'editor',
          },
        },
        {
          path: '/contrato',
          name: 'Contrato',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/contrato.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'Contratos',
            rule: 'editor',
          },
        },
        {
          path: '/tipoPagamento',
          name: 'Tipo de Pagamento',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/tipoPagamento.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'Tipo de Pagamentos',
            rule: 'editor',
          },
        },
        {
          path: '/tipoFotos',
          name: 'Tipo de Foto',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/tipoFotos.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'Tipo de Fotos',
            rule: 'editor',
          },
        },
        {
          path: '/integracoes',
          name: 'Integrações',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/integracoes.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'Integrações',
            rule: 'editor',
          },
        },
        {
          path: '/agravos',
          name: 'Agravos',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/agravos/agravosView.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Agravos',
                active: true,
              },
            ],
            pageTitle: 'Agravos',
            rule: 'editor',
          },
        },
        {
          path: '/vistoria-previa',
          name: 'Vistoria Prévia',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/vistoria-previa.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Vistoria Prévia',
                active: true,
              },
            ],
            pageTitle: 'Vistoria Prévia',
            rule: 'editor',
          },
        },
        {
          path: '/conta-corrente',
          name: 'Conta Corrente',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/conta-corrente/conta.corrente.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Conta Corrente',
                active: true,
              },
            ],
            pageTitle: 'Conta Corrente',
            rule: 'editor',
          },
        },
        {
          path: '/precificacao-auto',
          name: 'Precificação Auto',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/precificacao/precificacao.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Precificação Auto',
                active: true,
              },
            ],
            pageTitle: 'Precificação Auto',
            rule: 'editor',
          },
        },
        {
          path: '/blocklist-corretoras',
          name: 'Blocklist Corretoras',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/blocklist-corretoras.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Cadastro',
                active: true,
              },
            ],
            pageTitle: 'Blocklist Corretoras',
            rule: 'editor',
          },
        },
        {
          path: '/endosso-automovel',
          name: 'Endosso Automóvel',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/endorsement.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Endosso Automóvel',
                active: true,
              },
            ],
            pageTitle: 'Endosso Automóvel',
            rule: 'editor',
          },
        },
        {
          path: '/alteracao-de-status',
          name: 'Alteração de Status',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/alteracao-de-status.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.STATUS_EDIT,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Alteração de Status',
                active: true,
              },
            ],
            pageTitle: 'Alteração de Status',
            rule: 'editor',
          },
        },
        {
          path: '/origem-da-cotacao',
          name: 'Origem da Cotação',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/origin-system/OriginSystem.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.ORIGIN_SYSTEM,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Origem da Cotação',
                active: true,
              },
            ],
            pageTitle: 'Origem da Cotação',
            rule: 'editor',
            checkPermission: true,
          },
        },
        {
          path: '/i4pro',
          name: 'I4Pro',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/i4pro.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.I4PRO,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                active: true,
              },
            ],
            pageTitle: 'I4Pro',
            rule: 'editor',
          },
        },
        {
          path: '/i4pro/envio-em-massa',
          name: 'I4Pro Envio em Massa',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/configuracoes/i4pro-batches.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.I4PRO,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Configurações',
                url: '/i4pro',
              },
              {
                title: 'Envio em massa i4Pro',
                active: true,
              },
            ],
            pageTitle: 'Envio em massa i4Pro',
            rule: 'editor',
          },
        },
        {
          path: '/congeneres',
          name: 'Congêneres',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/configuracoes/congeners.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.CONGENERS,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Congêneres',
                active: true,
              },
            ],
            pageTitle: 'Congêneres',
            rule: 'editor',
            checkPermission: true,
          },
        },
        // =============================================================================
        // MENU ADMINISTRADOR (CONFIGURAÇÕES)
        // =============================================================================
        {
          path: '/grupoVeiculos',
          name: 'Grupo de Veículos',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/grupoVeiculos.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Grupos',
                active: true,
              },
            ],
            pageTitle: 'Grupo de Veículos',
            rule: 'editor',
          },
        },
        {
          path: '/listGruposModelos/:id',
          name: 'listGruposModelos',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/tabelas/grupoVeiculosModelos.vue'),
          meta: {
            permissionOptions:
              ROUTER_PERMISSIONS.VEHICLES_GROUP_ASSOCIATE_MODELS,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Grupos',
                url: '/grupoVeiculos',
              },
              {
                title: 'Modelos Associados',
                active: true,
              },
            ],
            pageTitle: 'Modelos Associados ao Grupo',
            rule: 'editor',
          },
        },
        {
          path: '/listGruposAcessorios/:id',
          name: 'listGruposAcessorios',
          beforeEnter: navigationGuard,
          component: () =>
            import('./views/menu/tabelas/grupoVeiculosAcessorios.vue'),
          meta: {
            permissionOptions:
              ROUTER_PERMISSIONS.VEHICLES_GROUP_ASSOCIATE_ACCESSORIES,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Grupos',
                url: '/grupoVeiculos',
              },
              {
                title: 'Acessorios Associados',
                active: true,
              },
            ],
            pageTitle: 'Acessorios Associados ao Grupo',
            rule: 'editor',
          },
        },
        {
          path: '/tabelas',
          name: 'Tabelas',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/tabelas.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Tabelas',
                active: true,
              },
            ],
            pageTitle: 'Tabelas',
            rule: 'editor',
          },
        },
        {
          path: '/listTabelaValores/:id',
          name: 'listTabelaValores',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/valores.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.TABLES_VALUES,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Tabelas',
                url: '/Tabelas',
              },
              {
                title: 'Valores',
                active: true,
              },
            ],
            pageTitle: 'Valores',
            rule: 'editor',
          },
        },
        {
          path: '/listTabelaEstadosCidades/:id',
          name: 'listTabelaEstadosCidades',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/estadosCidades.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.TABLES_LOCATIONS,
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Tabelas',
                url: '/Tabelas',
              },
              {
                title: 'Estados e Cidades',
                active: true,
              },
            ],
            pageTitle: 'Estados e Cidades',
            rule: 'editor',
          },
        },
        {
          path: '/blackList',
          name: 'Black List',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/blackList.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Tabelas',
                active: true,
              },
            ],
            pageTitle: 'Black List',
            rule: 'editor',
          },
        },
        {
          path: '/listRastreadores',
          name: 'Lista Rastreadores',
          beforeEnter: navigationGuard,
          component: () => import('./views/menu/tabelas/listRastreadores.vue'),
          meta: {
            breadcrumb: [
              {
                title: 'Home',
                url: '/',
              },
              {
                title: 'Tabelas',
                active: true,
              },
            ],
            pageTitle: 'Lista de Rastreadores',
            rule: 'editor',
          },
        },
        {
          path: '/401',
          name: 'pageError401',
          component: () => import('@/views/pages/Error401.vue'),
          meta: {
            checkPermission: false,
          },
        },
        {
          path: '/404',
          name: 'pageError404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            checkPermission: false,
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/Login',
          name: 'pageLogin',
          component: () => import('@/views/pages/Login.vue'),
        },
        {
          path: '/TrocarSenha',
          name: 'trocaSenha',
          component: () => import('@/views/pages/trocarSenha.vue'),
        },
        {
          path: '/boleto/:idRegistroFinanceiro',
          name: 'boleto',
          component: () => import('@/views/pages/Boleto.vue'),
        },
        {
          path: '/boleto/:idRegistroFinanceiro/:nossoNumero',
          name: 'boleto',
          component: () => import('@/views/pages/Boleto.vue'),
        },
        {
          path: '/boletos/:idApolice',
          name: 'boletos',
          component: () => import('@/views/pages/Todos-Boletos.vue'),
        },
        {
          path: '/contratoCotacao/:chave',
          name: 'contratoCotacao',
          component: () => import('@/views/pages/ContratoCotacao.vue'),
        },
        {
          path: 'endosso/contrato-cotacao/:chave',
          name: 'EndorsementQuotationContract',
          component: () =>
            import('@/views/pages/EndorsementQuotationContract.vue'),
        },
        {
          path: 'endosso/contrato-apolice/:chave',
          name: 'EndorsementPolicyContract',
          component: () =>
            import('@/views/pages/EndorsementPolicyContract.vue'),
        },
        {
          path: 'endosso/contrato-proposta/:chave',
          name: 'EndorsementProposalContract',
          component: () =>
            import('@/views/pages/EndorsementProposalContract.vue'),
        },
        {
          path: '/contratoProposta/:chave',
          name: 'contratoProposta',
          component: () => import('@/views/pages/ContratoAdesao.vue'),
        },
        {
          path: '/contratoPropostaVida/:chave',
          name: 'contratoPropostaVida',
          component: () => import('@/views/pages/ContratoAdesaoVida.vue'),
        },
        {
          path: '/laudoVistoria/:chave',
          name: 'laudoVistoria',
          beforeEnter: navigationGuard,
          component: () => import('@/views/pages/LaudoVistoria.vue'),
          meta: {
            permissionOptions: ROUTER_PERMISSIONS.INSPECTION_DETAIL,
            ignoreMaintenance: true,
          },
        },
        {
          path: '/maintenance',
          name: 'pageMaintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      name: 'error-404',
      component: () => import('@/views/pages/Error404.vue'),
    },
  ],
});

function navigationGuard(to, from, next) {
  if (!to.meta.ignoreAuth) {
    const checkAuth = isAuth(to, next);
    if (!checkAuth) {
      return;
    }
  }

  if (!to.meta.ignoreMaintenance) {
    const checkMaintenance = maintenance(next);
    if (!checkMaintenance) {
      return;
    }
  }
  next();
}

function isAuth(to, next) {
  const token = localStorage.getItem('user-token');
  localStorage.setItem('rotaEntrada', window.location.href.toString());
  if (!token) {
    next(`/Login`);
    return false;
  } else {
    const decoded = jwt.decode(token);
    if (!decoded) {
      next(`/Login`);
      return false;
    } else {
      //confere exp
      if (Date.now() >= decoded.exp * 1000) {
        next(`/Login`);
        return false;
      }
    }
  }
  return validatePermission(to, next);
}

function maintenance(next) {
  const maintenance = process.env.VUE_APP_MAINTENANCE;

  if (maintenance) {
    next('/maintenance');
    return false;
  } else {
    return true;
  }
}

function closeDialogs() {
  const dialog = document.querySelector('.con-vs-dialog');
  if (dialog) {
    dialog.remove();
  }
}

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
  closeDialogs();
});

function hasReadPermission(to, abilityInstance) {
  return READ_PERMISSIONS.some((permission) =>
    abilityInstance.can(permission, to.name),
  );
}

function validatePermission(to, next) {
  if (to.meta === undefined) return next();
  if (to.meta.checkPermission !== undefined) {
    if (!to.meta.checkPermission) {
      return true;
    }
  }
  const abilityInstance = ability.getPermission();
  const permissionsRequired = to.meta.permissionOptions;
  if (permissionsRequired && permissionsRequired.length) {
    const hasPermission = permissionsRequired.some(({ action, subject }) =>
      abilityInstance.can(action, subject),
    );
    return hasPermission ? next() : next(`/401`);
  }
  const hasSomeReadPermission = hasReadPermission(to, abilityInstance);
  if (hasSomeReadPermission) {
    return true;
  }

  next(`/401`);
  return false;
}

async function validateProposalStatus(to, next) {
  try {
    const statusId = await autoApi.getQuotationStatus(to.params.id);
    if (statusId === STATUS_PROPOSTA.COTACAO_ABERTA.id) {
      return next(`/auto/cotacao/${to.params.id}`);
    }

    const transmitted = functions.isAlreadyTransmitted(statusId);
    to.meta.pageTitle = transmitted
      ? 'Detalhes da Proposta'
      : 'Transmitir Proposta';

    to.meta.breadcrumb = [
      { title: 'Home', url: '/' },
      { title: 'Propostas', url: '/Cotacoes' },
      { title: to.meta.pageTitle, active: true },
    ];

    return next();
  } catch (error) {
    console.error(error);
    return next('/404');
  }
}

router.beforeResolve(async (to, from, next) => {
  if (to.name === 'editarProposta') {
    return await validateProposalStatus(to, next);
  }
  next();
});

export default router;
